"use client";

import axios from "axios";
import { ChangeEvent, useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";

import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";

import { LuTrash, LuX } from "react-icons/lu";
import { GrEdit } from "react-icons/gr";

import { Avatar } from "~/components/shadcn/Avatar";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetClose,
} from "~/components/v3/shadcn/Sheet";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/v3/shadcn/Form";

import { env } from "~/env.mjs";
import { cn } from "~/utils/fontsConfig";
import { RouterInputs, api } from "~/utils/api";

import Spinner from "~/components/themed/spinner";
import { Label, SubLabel } from "~/components/v3/shadcn/Label";
import { Input } from "~/components/v4/shadcn/Input";

import { TextArea } from "~/components/v4/shadcn/TextArea";
import { RadioGroup, RadioGroupItem } from "~/components/v3/shadcn/RadioGroup";

import {
    bgBannerColors,
    getDefaultCommunityFormValues,
} from "../_utils/CommunityPageUtils";
import {
    CommunityFormType,
    communityFormSchema,
} from "../_utils/CommunityPageTypes";
import { RiCloseLargeFill } from "react-icons/ri";
import { Button } from "~/components/v3/themed/Button";
import { GetCommunityDetailsAndExtraDetails } from "~/utils/community-queries";
import { TagsInput } from "~/components/v4/shadcn/TagsInput";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { ensureHttpsPrefix } from "~/utils/links";

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    community: GetCommunityDetailsAndExtraDetails["communityDetails"];
    onSuccess: () => void;
    isNewCommunityManagement?: boolean;
};

type FileType = "image" | "coverImage";
type FormData = RouterInputs["community"]["editCommunityDetails"];

const EditCommunitySheet = ({
    isOpen,
    setIsOpen,
    community,
    onSuccess,
    isNewCommunityManagement = false,
}: Props) => {
    const defaultValues = getDefaultCommunityFormValues(community);
    const form = useForm<CommunityFormType>({
        resolver: zodResolver(communityFormSchema),
        defaultValues: defaultValues,
    });

    useEffect(() => {
        form.reset(defaultValues);
    }, [community]);

    const router = useRouter();

    const avatarImage = form.watch("images.image");
    const bannerImage = form.watch("images.coverImage");

    const bannerStyle: React.CSSProperties = bannerImage?.startsWith("linear")
        ? {
              background: bannerImage || undefined,
          }
        : {
              backgroundImage: `url(${`${env.NEXT_PUBLIC_ASSETS_URL}/community/${community?.id}/${bannerImage}`})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
          };

    const bio = form.watch("description");
    const bioCharLength = 2048;

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileTypeRef = useRef<FileType>("image");
    const [isUpLoading, setIsUpLoading] = useState(false);
    const [fullyLoaded, setFullyLoaded] = useState(false);

    const mainLinks = useFieldArray({
        control: form.control,
        name: "mainLinks",
        rules: {
            minLength: 0,
            maxLength: 4,
        },
    });

    const socialLinks = useFieldArray({
        control: form.control,
        name: "socials",
        rules: {
            minLength: 0,
            maxLength: 4,
        },
    });

    useEffect(() => {
        if (community && !fullyLoaded) {
            form.reset(getDefaultCommunityFormValues(community) ?? {});
            form.trigger();
            setFullyLoaded(true);
        }
    }, [community, form.reset]);

    const update = api.community.editCommunityDetails.useMutation();

    const { mutateAsync: fetchPresignedUrls } =
        api.s3.getStandardUploadPresignedUrl.useMutation();

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !community) return;

        const displayText =
            fileTypeRef.current === "image" ? "Profile" : "Banner";

        toast.loading(`Uploading ${displayText} picture`, {
            toastId: "upload-picture",
        });

        setIsUpLoading(true);
        const file = e.target.files[0];
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const uploadFilename = `${(Math.random() + 1)
            .toString(36)
            .substring(7)}${ext}`;

        const presignedURL = await fetchPresignedUrls({
            key: `community/${community.id}/${uploadFilename}`,
        });

        let newFile = new File([file], uploadFilename, {
            type: file.type,
        });

        try {
            await axios.put(presignedURL, newFile.slice(), {
                headers: { "Content-Type": newFile.type },
            });

            form.setValue(`images.${fileTypeRef.current}`, uploadFilename);

            await update.mutateAsync(
                {
                    [fileTypeRef.current === "image" ? "image" : "coverImage"]:
                        uploadFilename,
                    communityId: community.id,
                    slug: community.slug,
                },
                {
                    onSuccess: () => {
                        toast.success(`${displayText} picture updated!`);
                    },
                    onError: () => {
                        toast.error(`Error updating ${displayText}  picture!`);
                    },
                    onSettled: () => {
                        toast.dismiss(`upload-picture`);
                    },
                },
            );
        } catch (error) {
            toast.error("Error uploading profile picture!");
            console.error(`Error updating profile picture`, error);
        }

        setIsUpLoading(false);
    };

    const onSubmit = async (data: CommunityFormType) => {
        if (!community?.id) {
            toast.error("Community not found");
            return;
        }

        const processedSocials = data.socials?.map((social) => ({
            ...social,
            url: ensureHttpsPrefix(social?.url || ""),
        }));
        const processedMainLinks = data.mainLinks?.map((link) => ({
            ...link,
            url: ensureHttpsPrefix(link?.url || ""),
        }));

        const toastId = "update-community";

        toast.loading("Updating community...", {
            toastId: toastId,
            autoClose: false,
        });

        const updatedData: FormData = {
            ...data,
            socials: processedSocials,
            mainLinks: processedMainLinks,
            image: data?.images?.image,
            coverImage: data?.images?.coverImage,
            bannersBg: data.bannersBg || null, // empty string -> null
        };

        await update.mutateAsync(
            {
                ...updatedData,
            },
            {
                onSuccess: (community) => {
                    toast.success("Community updated!", {
                        toastId: toastId,
                        autoClose: 1000,
                        isLoading: false,
                    });
                    if (!isNewCommunityManagement) {
                        router.push(`/beta/${community.slug}`);
                    }
                },
                onError: () => {
                    toast.error("Error updating community", {
                        toastId: toastId,
                        autoClose: 4000,
                        isLoading: false,
                    });
                },
                onSettled: () => {
                    toast.dismiss("update-community");
                    setIsOpen(false);
                },
            },
        );

        onSuccess();
    };

    const triggerFileUpload = (type: FileType) => {
        fileTypeRef.current = type;
        fileInputRef?.current?.click();
    };

    const updateBannerColor = (val: string | null) => {
        form.setValue("images.coverImage", val);
    };

    return (
        <Sheet open={isOpen} onOpenChange={() => setIsOpen(false)}>
            <SheetContent
                showCloseIcon={false}
                className="w-full overflow-y-scroll rounded-br-md rounded-tr-md border-none bg-[#121418] pb-20 md:min-w-[500px]"
            >
                <SheetHeader>
                    <div className="flex w-full items-center justify-between px-6 py-4">
                        <SheetTitle className="text-lg">
                            Edit Community
                        </SheetTitle>

                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <div
                    style={bannerStyle}
                    className={`relative h-36 w-full bg-gray-600 ${bannerImage}`}
                >
                    <button
                        className={cn(
                            "absolute bottom-2 right-2 z-50 cursor-pointer rounded-[0.6rem] bg-black/85 p-2",
                            "ease-in active:scale-95",
                            {
                                "cursor-not-allowed": isUpLoading,
                            },
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            triggerFileUpload("coverImage");
                        }}
                        disabled={isUpLoading}
                    >
                        <GrEdit className="text-xs text-white" />
                    </button>

                    <div className="absolute bottom-1 left-4 flex flex-col gap-2">
                        <div>
                            <Avatar className="relative h-[4.5rem] w-[4.5rem] overflow-visible border-2 border-primary transition-[border-color] duration-300 group-hover:border-secondary">
                                {!isUpLoading ? (
                                    <CommunityAvatar
                                        community={{
                                            id: community?.id ?? "",
                                            name: community?.name,
                                            image: community?.image,
                                        }}
                                        className="h-full w-full"
                                    />
                                ) : (
                                    <div className="flex w-full items-center justify-center">
                                        <Spinner className="h-5 w-5" />
                                    </div>
                                )}
                                <button
                                    className={cn(
                                        "absolute bottom-0 right-[-5px] z-50 cursor-pointer rounded-[0.6rem] bg-black/80 p-2",
                                        "active:scale-95",
                                        {
                                            "cursor-not-allowed": isUpLoading,
                                        },
                                    )}
                                    onClick={(e) => {
                                        if (isUpLoading) return;

                                        e.stopPropagation();
                                        triggerFileUpload("image");
                                    }}
                                    disabled={isUpLoading}
                                >
                                    <GrEdit className="text-xs text-white" />
                                </button>
                            </Avatar>
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={(e) => {
                                    handleFileChange(e);
                                }}
                                accept="image/png, image/gif, image/jpeg, image/webp"
                                hidden
                            />
                        </div>
                    </div>
                </div>

                <SheetDescription className="flex flex-col gap-6 p-6">
                    <div className="flex flex-col gap-2">
                        <Label
                            variant="secondary"
                            className="text-sm font-normal tracking-wide text-[#A3A8B4]"
                        >
                            Background image
                        </Label>

                        <div className="flex items-center gap-2">
                            <RadioGroup
                                className="flex gap-1"
                                defaultValue={`${bannerImage}`}
                                onValueChange={(e) => updateBannerColor(e)}
                            >
                                {bgBannerColors.map((item) => (
                                    <RadioGroupItem
                                        id={item.id}
                                        key={item.id}
                                        value={item.value}
                                        className={`h-7 w-7`}
                                        style={{
                                            background: item.value,
                                        }}
                                        showIndicatorIcon={false}
                                    />
                                ))}
                            </RadioGroup>
                            <RiCloseLargeFill
                                className="h-7 w-7 cursor-pointer rounded-full border-2 border-black text-red-700"
                                onClick={() => updateBannerColor(null)}
                            />
                        </div>
                    </div>

                    {!isNewCommunityManagement && (
                        <div className="flex flex-col gap-2">
                            <Label
                                variant="secondary"
                                className="text-sm font-normal tracking-wide text-[#A3A8B4]"
                            >
                                Banners background
                            </Label>

                            <div className="flex items-center gap-2">
                                <RadioGroup
                                    className="flex gap-1"
                                    defaultValue={
                                        community?.bannersBg ?? undefined
                                    }
                                    onValueChange={(e) =>
                                        form.setValue("bannersBg", e)
                                    }
                                >
                                    {bgBannerColors.map((item) => (
                                        <RadioGroupItem
                                            id={item.id}
                                            key={item.id}
                                            value={item.value}
                                            className={`h-7 w-7`}
                                            style={{
                                                background: item.value,
                                            }}
                                            showIndicatorIcon={false}
                                        />
                                    ))}
                                    <RadioGroupItem
                                        value={""}
                                        className={`relative h-7 w-7`}
                                        showIndicatorIcon={false}
                                    >
                                        <RiCloseLargeFill className="absolute left-[-1px] top-[-2px] h-7 w-7 cursor-pointer rounded-full text-red-700" />
                                    </RadioGroupItem>
                                </RadioGroup>
                            </div>
                        </div>
                    )}

                    <Form {...form}>
                        <form
                            className="flex flex-col gap-4"
                            onSubmit={form.handleSubmit(onSubmit)}
                        >
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                            Name
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="rounded-[0.626rem] tracking-wide"
                                                placeholder="Enter Community Name"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="slug"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                            Community URL
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="rounded-[0.626rem] tracking-wide"
                                                placeholder="Enter Community URL"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="mt-4 flex flex-col gap-4">
                                <div className="flex flex-col gap-1">
                                    <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                        Main Links
                                    </Label>
                                    <SubLabel className="text-sm text-muted-foreground">
                                        Main links show below the community
                                        description
                                    </SubLabel>
                                </div>

                                <div className="flex flex-col gap-4">
                                    {mainLinks.fields?.map((link, idx) => (
                                        <div
                                            className="flex items-end gap-2"
                                            key={link.id}
                                        >
                                            <FormField
                                                control={form.control}
                                                name={`mainLinks.${idx}.name`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <>
                                                                <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                                                    Name
                                                                </Label>
                                                                <Input
                                                                    {...field}
                                                                    value={
                                                                        field.value ??
                                                                        link?.name ??
                                                                        ""
                                                                    }
                                                                    className="rounded-[0.626rem] tracking-wide"
                                                                    placeholder="Discord"
                                                                />
                                                            </>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name={`mainLinks.${idx}.url`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <>
                                                                <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                                                    Url
                                                                </Label>
                                                                <Input
                                                                    {...field}
                                                                    value={
                                                                        field.value ??
                                                                        link?.url ??
                                                                        ""
                                                                    }
                                                                    className="rounded-[0.626rem] tracking-wide"
                                                                    placeholder="https://discord.com/my-community"
                                                                />
                                                            </>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <Button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    mainLinks.remove(idx);
                                                }}
                                                className="rounded-[0.626rem]"
                                                variant="destructive"
                                            >
                                                <LuTrash className="text-white" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        mainLinks.append({ name: "", url: "" });
                                    }}
                                    className="w-full rounded-[0.626rem]"
                                    variant="secondary"
                                    disabled={mainLinks.fields.length >= 4}
                                >
                                    Add link
                                </Button>
                            </div>

                            <div className="mt-4 flex flex-col gap-4">
                                <div className="flex flex-col gap-1">
                                    <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                                        Social Links
                                    </Label>
                                    <SubLabel className="text-sm text-muted-foreground">
                                        Social links show at the top right of
                                        the community header
                                    </SubLabel>
                                </div>
                                <div className="flex flex-col gap-4">
                                    {socialLinks.fields?.map(
                                        (socialLink, idx) => (
                                            <div
                                                className="flex items-center gap-2"
                                                key={idx}
                                            >
                                                <FormField
                                                    control={form.control}
                                                    name={`socials.${idx}.url`}
                                                    key={socialLink.id}
                                                    render={({
                                                        field: {
                                                            value,
                                                            ...field
                                                        },
                                                    }) => (
                                                        <FormItem className="w-full">
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    value={
                                                                        value ??
                                                                        ""
                                                                    }
                                                                    className="rounded-[0.626rem] tracking-wide"
                                                                    placeholder="https://youtube.com/my-community"
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <Button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        socialLinks.remove(idx);
                                                    }}
                                                    className="rounded-[0.626rem]"
                                                    variant="destructive"
                                                >
                                                    <LuTrash className="text-white" />
                                                </Button>
                                            </div>
                                        ),
                                    )}
                                </div>
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        socialLinks.append({ url: "" });
                                    }}
                                    className="w-full rounded-[0.626rem]"
                                    variant="secondary"
                                    disabled={socialLinks.fields.length >= 4}
                                >
                                    Add social link
                                </Button>
                            </div>

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="mt-4 text-sm font-normal text-[#A3A8B4]">
                                            Community
                                        </FormLabel>
                                        <FormControl>
                                            <TextArea
                                                {...field}
                                                value={field.value ?? ""}
                                                className="h-20 w-full rounded-[0.626rem]"
                                                placeholder="Tell me about yourself"
                                            ></TextArea>
                                        </FormControl>

                                        <FormDescription className="my-1">
                                            {bio?.length ?? 0} / {bioCharLength}
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="categories"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="mt-4 flex flex-col text-sm font-normal text-[#A3A8B4]">
                                            Categories
                                            <SubLabel className="text-sm text-muted-foreground">
                                                Comma separated list of
                                                categories
                                            </SubLabel>
                                        </FormLabel>
                                        <FormControl>
                                            <TagsInput
                                                value={field.value ?? []}
                                                onChange={field.onChange}
                                                suggestions={[
                                                    "business",
                                                    "sport",
                                                ]}
                                                placeholder="Add category..."
                                                emptyMessage="No matches found."
                                                className="rounded-[0.625rem]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="flex justify-end py-1">
                                <Button
                                    className={cn(
                                        "p-1 px-4 disabled:cursor-not-allowed",
                                    )}
                                    variant="secondary"
                                    type="submit"
                                    disabled={form.formState.isSubmitting}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </form>
                    </Form>
                </SheetDescription>
            </SheetContent>
        </Sheet>
    );
};

export default EditCommunitySheet;
